import { useState } from 'react';
import { useToast } from '../../../hooks/useToast';
import { uploadCandidate, uploadJob, analyzeCandidateJob } from '../../../services';
import { ApiError } from '../../../lib/errors';
import type { BulkAnalysisInput, BulkAnalysisResult } from '../types/bulk';
import { supabase } from '../../../lib/supabase';

export function useBulkAnalysis() {
  const [results, setResults] = useState<BulkAnalysisResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading] = useState(false);
  const { addToast } = useToast();

  const bulkAnalyze = async (input: BulkAnalysisInput): Promise<BulkAnalysisResult[]> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new ApiError('Not authenticated', 401);
      }
      
      const userId = session.user.id;
      const analysisResults: BulkAnalysisResult[] = [];
      const totalFiles = input.files.length;
      
      // Upload job description once
      const job = await uploadJob(
        input.jobTitle,
        input.jobDescription,
        '' // company (optional parameter)
      );
      
      console.log('Job uploaded for bulk analysis:', job);

      // Process each CV file
      for (let i = 0; i < input.files.length; i++) {
        const fileData = input.files[i];
        const startTime = Date.now();
        
        try {
          // Update progress
          if (input.onProgress) {
            input.onProgress({
              overall: (i / totalFiles) * 100,
              currentIndex: i,
              currentProgress: 0,
              currentFileName: fileData.name,
              estimatedTimeRemaining: calculateEstimatedTime(i, totalFiles, startTime)
            });
          }

          // Upload candidate
          const candidateData = {
            name: fileData.extractedName || `Candidate ${i + 1}`,
            cv_text: fileData.cvText,
            created_at: new Date().toISOString(),
            user_id: userId
          };

          const candidate = await uploadCandidate(candidateData);
          
          // Update progress
          if (input.onProgress) {
            input.onProgress({
              overall: ((i + 0.3) / totalFiles) * 100,
              currentIndex: i,
              currentProgress: 30,
              currentFileName: fileData.name
            });
          }

          // Analyze candidate against job
          const analysis = await analyzeCandidateJob({
            candidateId: candidate.id,
            jobId: job.id
          });

          // Update progress
          if (input.onProgress) {
            input.onProgress({
              overall: ((i + 0.8) / totalFiles) * 100,
              currentIndex: i,
              currentProgress: 80,
              currentFileName: fileData.name
            });
          }

          // Transform analysis to bulk result format
          const result: BulkAnalysisResult = {
            id: analysis.id,
            fileName: fileData.name,
            candidateName: fileData.extractedName || candidateData.name,
            overallScore: analysis.fit_score,
            technicalScore: calculateTechnicalScore(analysis.technical_skills),
            experienceScore: calculateExperienceScore(analysis.reasoning),
            culturalFitScore: calculateCulturalScore(analysis.growth_potential),
            verdict: convertVerdict(analysis.verdict),
            strengths: analysis.reasoning?.slice(0, 3) || [],
            weaknesses: analysis.risk_factors?.map(rf => rf.description).slice(0, 3) || [],
            matchedSkills: analysis.technical_skills?.map(ts => ts.skill) || [],
            missingSkills: extractMissingSkills(analysis.reasoning),
            riskFactors: (analysis.risk_factors || []).map(rf => ({
              type: 'general',
              description: rf.description || '',
              severity: (rf.severity?.toLowerCase() as 'low' | 'medium' | 'high') || 'medium'
            })),
            interviewQuestions: analysis.interview_plan ? [analysis.interview_plan] : [],
            reasoning: analysis.reasoning || [],
            analysisDate: new Date(analysis.created_at),
            processingTime: Date.now() - startTime
          };

          analysisResults.push(result);
          
          // Update progress to completed
          if (input.onProgress) {
            input.onProgress({
              overall: ((i + 1) / totalFiles) * 100,
              currentIndex: i,
              currentProgress: 100,
              currentFileName: fileData.name
            });
          }

        } catch (fileError) {
          console.error(`Error processing file ${fileData.name}:`, fileError);
          
          // Add error result
          const errorResult: BulkAnalysisResult = {
            id: `error-${Date.now()}-${i}`,
            fileName: fileData.name,
            candidateName: fileData.extractedName || `Candidate ${i + 1}`,
            overallScore: 0,
            technicalScore: 0,
            experienceScore: 0,
            culturalFitScore: 0,
            verdict: 'not_recommended',
            strengths: [],
            weaknesses: [],
            matchedSkills: [],
            missingSkills: [],
            riskFactors: [],
            interviewQuestions: [],
            reasoning: [],
            analysisDate: new Date(),
            processingTime: Date.now() - startTime,
            error: fileError instanceof Error ? fileError.message : 'Unknown error occurred'
          };
          
          analysisResults.push(errorResult);
        }
      }

      setResults(analysisResults);
      setError(null);
      
      const successCount = analysisResults.filter(r => !r.error).length;
      const errorCount = analysisResults.filter(r => r.error).length;
      
      addToast(
        `Bulk analysis completed: ${successCount} successful, ${errorCount} failed`,
        errorCount > 0 ? 'info' : 'success'
      );
      
      return analysisResults;

    } catch (err: any) {
      console.error('Bulk analysis error:', err);
      
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'Bulk analysis failed. Please try again.';

      setError(errorMessage);
      addToast(errorMessage, 'error');
      throw err;
    }
  };

  // Helper functions
  const convertVerdict = (verdict: any): 'strong_fit' | 'potential_fit' | 'needs_consideration' | 'not_recommended' => {
    if (typeof verdict === 'string') {
      switch (verdict.toUpperCase()) {
        case 'STRONG_FIT':
          return 'strong_fit';
        case 'POTENTIAL_FIT':
          return 'potential_fit';
        case 'NEEDS_CONSIDERATION':
          return 'needs_consideration';
        case 'NOT_RECOMMENDED':
          return 'not_recommended';
        default:
          return 'needs_consideration';
      }
    }
    return 'needs_consideration';
  };

  const calculateTechnicalScore = (technicalSkills: any): number => {
    if (!technicalSkills || !Array.isArray(technicalSkills)) return 0;
    
    const avgScore = technicalSkills.reduce((sum, skill) => {
      return sum + (skill.proficiency_level || 5);
    }, 0) / Math.max(technicalSkills.length, 1);
    
    return Math.min(avgScore * 2, 10); // Convert to 0-10 scale
  };

  const calculateExperienceScore = (reasoning: string[]): number => {
    if (!reasoning || !Array.isArray(reasoning)) return 0;
    
    const experienceKeywords = ['experience', 'years', 'senior', 'lead', 'expert', 'veteran'];
    const experienceReasons = reasoning.filter(reason => 
      experienceKeywords.some(keyword => 
        reason.toLowerCase().includes(keyword)
      )
    );
    
    return Math.min(experienceReasons.length * 2.5, 10);
  };

  const calculateCulturalScore = (growthPotential: any): number => {
    if (!growthPotential) return 7; // Default cultural fit score
    
    if (typeof growthPotential === 'object' && growthPotential.score) {
      return Math.min(growthPotential.score, 10);
    }
    
    return 7;
  };

  const extractMissingSkills = (reasoning: string[]): string[] => {
    if (!reasoning || !Array.isArray(reasoning)) return [];
    
    const missingSkillsReasons = reasoning.filter(reason =>
      reason.toLowerCase().includes('missing') || 
      reason.toLowerCase().includes('lack') ||
      reason.toLowerCase().includes('need')
    );
    
    // Extract skills mentioned in missing context
    const commonSkills = [
      'React', 'Angular', 'Vue', 'Node.js', 'Python', 'Java', 'C#', '.NET',
      'AWS', 'Azure', 'Docker', 'Kubernetes', 'TypeScript', 'JavaScript',
      'SQL', 'NoSQL', 'MongoDB', 'PostgreSQL', 'Redis', 'Git', 'CI/CD'
    ];
    
    const missingSkills: string[] = [];
    missingSkillsReasons.forEach(reason => {
      commonSkills.forEach(skill => {
        if (reason.toLowerCase().includes(skill.toLowerCase()) && 
            !missingSkills.includes(skill)) {
          missingSkills.push(skill);
        }
      });
    });
    
    return missingSkills.slice(0, 5); // Limit to 5 missing skills
  };

  const calculateEstimatedTime = (currentIndex: number, totalFiles: number, startTime: number): number => {
    if (currentIndex === 0) return 0;
    
    const elapsedTime = Date.now() - startTime;
    const avgTimePerFile = elapsedTime / currentIndex;
    const remainingFiles = totalFiles - currentIndex;
    
    return Math.round((remainingFiles * avgTimePerFile) / 1000); // Return in seconds
  };

  const resetResults = () => {
    setResults([]);
    setError(null);
  };

  return {
    results,
    error,
    isLoading,
    bulkAnalyze,
    resetResults
  };
} 